/**
 * Маршрут мастерства: ступень проходится не числом уроков, а памятью.
 *
 * У каждой фразы ученика — своя память (FSRS): каждое задание на фразу —
 * в уроке, в закреплении, в повторении кармана — ставит ей оценку. По
 * оценке и вероятности вспомнить сейчас фраза «новая», «на повторении»,
 * «хорошо» или «отлично» (`phraseGrade`).
 *
 * Ступень пройдена, когда пройден её курс (уроки, открытые тарифом) и
 * все её фразы — на «хорошо» и «отлично» (`levelMastery`). Пробелы есть —
 * дальше идут закрепления, пока не закроются. Сколько уроков до следующей
 * ступени, ученик не видит: только как растёт освоенность.
 *
 * Что делать дальше, решает планировщик (`planNextStep`): новый урок,
 * закрепление (долг памяти вырос или подошёл срок свести новое), проверка
 * знаний (ученик щёлкает уроки — пусть докажет, что следующий блок уже
 * знает, и пропустит его) или финиш. Частота закреплений — по успеваемости
 * и нагрузке ученика, порядок заданий — по типу мышления.
 *
 * Чистые функции: зовут сервер (решения и итоги) и тесты.
 */

import { z } from 'zod';

import type { VocabLoad } from './adaptivePlan';
import type { DrillDifficulty } from './drillAbility';
import { DrillAnswerSchema, type CurriculumLevel, type Drill, type DrillType } from './schemas/curriculum';
import type { PhraseChunk } from './schemas/phrase';
import type { Strategy } from './types';

/* ── Оценка фразы ────────────────────────────────────────────────── */

/** Оценка FSRS: 1 — не вспомнил, 2 — с трудом, 3 — хорошо, 4 — легко. */
export type MemoryRating = 1 | 2 | 3 | 4;

/** Первая попытка задания на фразу. */
export interface PhraseAttempt {
  readonly correct: boolean;
  /** Уровень варианта задания: −2 (проще) … 2 (сложнее). */
  readonly difficulty: number;
  readonly timeMs?: number;
}

/** Быстрее этого — ответ «с ходу», без раздумий. */
export const FAST_ANSWER_MS = 6_000;

/**
 * Оценка фразы по первым попыткам заданий на неё. Все верно — «хорошо»,
 * а на сложных вариантах или быстро — «легко»; хотя бы половина — «с
 * трудом»; меньше — «не вспомнил». Заданий не было — оценки нет.
 */
export function ratingFromAttempts(attempts: readonly PhraseAttempt[]): MemoryRating | null {
  if (attempts.length === 0) return null;
  const correct = attempts.filter((attempt) => attempt.correct).length;
  if (correct === attempts.length) {
    const hard = attempts.reduce((sum, attempt) => sum + attempt.difficulty, 0) / attempts.length >= 1;
    const timed = attempts.flatMap((attempt) => (attempt.timeMs === undefined ? [] : [attempt.timeMs]));
    const fast = timed.length > 0 && timed.reduce((sum, ms) => sum + ms, 0) / timed.length <= FAST_ANSWER_MS;
    return hard || fast ? 4 : 3;
  }
  return correct * 2 >= attempts.length ? 2 : 1;
}

/* ── Память фразы ────────────────────────────────────────────────── */

export const PHRASE_GRADES = ['new', 'weak', 'good', 'excellent'] as const;
export type PhraseGrade = (typeof PHRASE_GRADES)[number];

/** Память фразы — из карточки FSRS. */
export interface PhraseMemory {
  /** Фраза хоть раз оценена (карточка не «новая»). */
  readonly rated: boolean;
  readonly lastRating: MemoryRating | null;
  /** Вероятность вспомнить сейчас, 0–1. */
  readonly retrievability: number;
  /** Устойчивость памяти, дней (интервал до 90% припоминания). */
  readonly stabilityDays: number;
}

/** Ниже — фраза снова «на повторении», даже если в прошлый раз было «хорошо». */
export const GOOD_RECALL = 0.8;
/** Память держит фразу две недели — это уже «отлично». */
export const EXCELLENT_STABILITY_DAYS = 14;
/** «Легко» в прошлый раз и вспомнит сейчас с такой вероятностью — тоже «отлично». */
const EXCELLENT_RECALL = 0.9;

export function phraseGrade(memory: PhraseMemory): PhraseGrade {
  if (!memory.rated || memory.lastRating === null) return 'new';
  if (memory.lastRating <= 2 || memory.retrievability < GOOD_RECALL) return 'weak';
  if (memory.stabilityDays >= EXCELLENT_STABILITY_DAYS || (memory.lastRating === 4 && memory.retrievability >= EXCELLENT_RECALL)) return 'excellent';
  return 'good';
}

/* ── Мастерство ступени ──────────────────────────────────────────── */

/** Фраза ступени для подсчёта мастерства. */
export interface LevelPhrase {
  readonly grade: PhraseGrade;
  /** Сколько раз фраза забывалась после «хорошо». */
  readonly lapses: number;
}

/**
 * «Трудная» фраза: забывалась столько раз. Такие не держат ученика на
 * ступени вечно — их прощается до `LEECH_ALLOWANCE` от числа фраз
 * ступени; в кармане они повторяются дальше.
 */
export const LEECH_LAPSES = 4;
export const LEECH_ALLOWANCE = 0.05;

export interface LevelMastery {
  readonly total: number;
  readonly excellent: number;
  readonly good: number;
  readonly weak: number;
  readonly fresh: number;
  /** Слабые фразы, которые прощаются как «трудные». */
  readonly forgiven: number;
  /** Доля фраз на «хорошо» и «отлично», 0–1. */
  readonly share: number;
  /** Все фразы ступени — на «хорошо» и «отлично» (кроме прощённых трудных). */
  readonly mastered: boolean;
}

export function levelMastery(phrases: readonly LevelPhrase[]): LevelMastery {
  const count = (grade: PhraseGrade): number => phrases.filter((phrase) => phrase.grade === grade).length;
  const total = phrases.length;
  const excellent = count('excellent');
  const good = count('good');
  const weak = count('weak');
  const fresh = count('new');
  const leeches = phrases.filter((phrase) => phrase.grade === 'weak' && phrase.lapses >= LEECH_LAPSES).length;
  const forgiven = Math.min(leeches, Math.floor(total * LEECH_ALLOWANCE));
  return {
    total,
    excellent,
    good,
    weak,
    fresh,
    forgiven,
    share: total === 0 ? 0 : (excellent + good) / total,
    mastered: total > 0 && fresh === 0 && weak - forgiven === 0,
  };
}

/* ── Закрепление ─────────────────────────────────────────────────── */

/** Фраза-кандидат в закрепление. */
export interface ReviewCandidate {
  readonly id: string;
  readonly grade: PhraseGrade;
  readonly retrievability: number;
  /** Фраза текущей ступени ученика. */
  readonly currentLevel: boolean;
}

/** Фраз в одном закреплении — по нагрузке ученика. */
export const REVIEW_SIZE: Readonly<Record<VocabLoad, number>> = { REDUCED: 6, NORMAL: 8, EXPANDED: 10 };

/**
 * Что закреплять: сначала слабые (текущей ступени — раньше, самые
 * забываемые — первыми), затем ещё не оценённые фразы ступени, затем
 * «хорошие», которые пора повторить (вспомнит с вероятностью ниже 90%).
 */
export function pickReviewPhrases(candidates: readonly ReviewCandidate[], size: number): readonly ReviewCandidate[] {
  const byRecall = (a: ReviewCandidate, b: ReviewCandidate): number => Number(b.currentLevel) - Number(a.currentLevel) || a.retrievability - b.retrievability;
  const weak = candidates.filter((item) => item.grade === 'weak').sort(byRecall);
  const fresh = candidates.filter((item) => item.grade === 'new' && item.currentLevel);
  const due = candidates.filter((item) => (item.grade === 'good' || item.grade === 'excellent') && item.retrievability < EXCELLENT_RECALL).sort(byRecall);
  return [...weak, ...fresh, ...due].slice(0, size);
}

/** Сколько фраз сейчас ждут закрепления: слабые и пора повторить. */
export function memoryDebt(candidates: readonly ReviewCandidate[]): number {
  return candidates.filter((item) => item.grade === 'weak' || ((item.grade === 'good' || item.grade === 'excellent') && item.retrievability < EXCELLENT_RECALL)).length;
}

/**
 * Порядок заданий закрепления по типу мышления. Индуктивный ученик идёт от
 * узнавания к производству (выбрать → собрать → перевести → сказать);
 * дедуктивный — сразу вспоминает и производит, а узнавание — в конце.
 */
export function reviewDrillOrder(strategy: Strategy): readonly DrillType[] {
  return strategy === 'INDUCTIVE' ? ['CHOICE', 'DRAG_ORDER', 'TRANSLATE_CHUNK', 'FLUENCY'] : ['TRANSLATE_CHUNK', 'FLUENCY', 'DRAG_ORDER', 'CHOICE'];
}

/* ── Планировщик ─────────────────────────────────────────────────── */

export type NextStep =
  /** Новый урок курса. */
  | { readonly kind: 'LESSON'; readonly skillId: string }
  /** Закрепление: долг памяти, пора свести новое или пробелы ступени. */
  | { readonly kind: 'REVIEW'; readonly reason: 'MEMORY_DEBT' | 'CONSOLIDATE' | 'LEVEL_GAPS'; readonly phrases: number }
  /** Проверка знаний: сдал — уроки блока засчитаны без прохождения. */
  | { readonly kind: 'CHECK'; readonly skillIds: readonly string[] }
  /** Программа языка пройдена целиком. */
  | { readonly kind: 'DONE' };

export interface PlannerInput {
  /** Следующий непройденный урок курса ступени; `null` — курс пройден. */
  readonly nextLesson: string | null;
  readonly mastery: LevelMastery;
  /** Фраз ждут закрепления (`memoryDebt`) по всем ступеням. */
  readonly debt: number;
  /** Новых уроков после прошлого закрепления. */
  readonly newSinceReview: number;
  /** Средняя точность первой попытки последних уроков; `null` — уроков не было. */
  readonly recentAccuracy: number | null;
  readonly load: VocabLoad;
  /** Уроков подряд на «отлично» после прошлой проверки знаний. */
  readonly fastStreak: number;
  /** Уроки, которые можно сдать проверкой (блок уже начатой темы). */
  readonly checkable: readonly string[];
}

/** Долг памяти, после которого новое не даём — сначала закрепить. */
export const DEBT_LIMIT: Readonly<Record<VocabLoad, number>> = { REDUCED: 6, NORMAL: 10, EXPANDED: 14 };
/** Минимум фраз, ради которого стоит собирать закрепление. */
export const MIN_REVIEW = 3;
/** Уроков на «отлично» подряд, чтобы предложить проверку знаний. */
export const FAST_STREAK_FOR_CHECK = 3;
/** «Отлично» — точность первой попытки не ниже. */
export const FAST_ACCURACY = 0.95;

/** Сколько новых уроков подряд, прежде чем свести их закреплением, — по успеваемости. */
export function consolidateEvery(recentAccuracy: number | null): number {
  if (recentAccuracy === null) return 3;
  if (recentAccuracy < 0.75) return 2;
  if (recentAccuracy < 0.9) return 3;
  return 5;
}

/**
 * Ступень пройдена: курс позади и все фразы — на «хорошо» и «отлично».
 * Фраз ступени в кармане нет вовсе (такое бывает только у старых данных) —
 * держать ученика нечем, достаточно курса.
 */
export function levelPassed(courseComplete: boolean, mastery: LevelMastery): boolean {
  return courseComplete && (mastery.mastered || mastery.total === 0);
}

export function planNextStep(input: PlannerInput): NextStep {
  const gaps = input.mastery.weak - input.mastery.forgiven + input.mastery.fresh;
  if (input.nextLesson === null) {
    // Курс ступени пройден: дальше — только закрепление, пока ступень не
    // освоена. Освоенную ступень сервер переключает раньше (`levelPassed`),
    // так что сюда освоенной приходит только последняя.
    return levelPassed(true, input.mastery) ? { kind: 'DONE' } : { kind: 'REVIEW', reason: 'LEVEL_GAPS', phrases: Math.max(gaps, MIN_REVIEW) };
  }
  if (input.debt >= DEBT_LIMIT[input.load]) return { kind: 'REVIEW', reason: 'MEMORY_DEBT', phrases: input.debt };
  if (input.newSinceReview >= consolidateEvery(input.recentAccuracy) && input.debt >= MIN_REVIEW) {
    return { kind: 'REVIEW', reason: 'CONSOLIDATE', phrases: input.debt };
  }
  if (input.fastStreak >= FAST_STREAK_FOR_CHECK && input.checkable.length > 0 && input.debt < MIN_REVIEW) {
    return { kind: 'CHECK', skillIds: input.checkable };
  }
  return { kind: 'LESSON', skillId: input.nextLesson };
}

/** Проверка знаний сдана: верных первых попыток не меньше. */
export const CHECK_PASS = 0.9;
/** Уроков в одной проверке знаний. */
export const CHECK_LESSONS = 2;

/* ── API ─────────────────────────────────────────────────────────── */

/** Мастерство текущей ступени ученика — вместо «урок N из 120». */
export interface LevelMasteryView extends LevelMastery {
  readonly level: CurriculumLevel;
  /** Уроки курса ступени (открытые тарифом) пройдены. */
  readonly courseComplete: boolean;
  /**
   * Путь до следующей ступени, 0–1: пройденная доля курса × доля освоенных
   * фраз. 1 — ровно когда ступень переключается; после первого урока это
   * не «100%», хотя три его фразы и освоены.
   */
  readonly progress: number;
}

/** Путь до следующей ступени (`LevelMasteryView.progress`). */
export function levelProgress(courseDone: number, courseTotal: number, mastery: LevelMastery): number {
  const course = courseTotal === 0 ? 1 : Math.min(1, courseDone / courseTotal);
  return course * (mastery.total === 0 ? 1 : mastery.share);
}

/** Ступень сменилась только что: откуда и куда. */
export interface LevelChange {
  readonly from: CurriculumLevel;
  readonly to: CurriculumLevel;
}

export interface NextStepView {
  readonly step: NextStep;
  readonly mastery: LevelMasteryView;
}

export const PRACTICE_KINDS = ['REVIEW', 'CHECK'] as const;
export type PracticeKind = (typeof PRACTICE_KINDS)[number];

export const StartPracticeSchema = z.object({ kind: z.enum(PRACTICE_KINDS) });
export type StartPractice = z.infer<typeof StartPracticeSchema>;

/** Закрепление или проверка знаний: фразы и задания на них. */
export interface PracticeView {
  readonly sessionId: string;
  readonly kind: PracticeKind;
  readonly level: CurriculumLevel;
  /** Фразы: у заданий `chunkId` ссылается сюда (картинка, озвучка). */
  readonly chunks: readonly PhraseChunk[];
  readonly drills: readonly Drill[];
  readonly strategy: Strategy;
  readonly guided: boolean;
  readonly startDifficulties: Partial<Record<DrillType, DrillDifficulty>>;
}

export const CompletePracticeSchema = z.object({
  timeSpentMs: z.number().int().nonnegative(),
  answers: z.array(DrillAnswerSchema).min(1).max(48),
});
export type CompletePractice = z.infer<typeof CompletePracticeSchema>;

/** Как изменилась память фразы за сеанс. */
export interface PracticePhraseResult {
  readonly phrase: string;
  readonly translation: string;
  readonly before: PhraseGrade;
  readonly after: PhraseGrade;
}

export interface PracticeResult {
  readonly kind: PracticeKind;
  /** Доля верных первых попыток. */
  readonly accuracy: number;
  /** Проверка знаний сдана; у закрепления — `null`. */
  readonly passed: boolean | null;
  readonly phrases: readonly PracticePhraseResult[];
  /** Уроки, засчитанные сданной проверкой. */
  readonly skipped: readonly string[];
  readonly levelUp: LevelChange | null;
  readonly mastery: LevelMasteryView;
  readonly next: NextStep;
}
